import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import {
  AMHS_DETAIL_CHECK_INTERVAL_S,
  horizontalDistanceM,
  resolveAmhsDetailFromDistance,
  type AmhsDetailLevel,
} from './amhsDetailLod';

/**
 * Throttled distance LOD for a single world anchor.
 * Updates React state only when the tier changes (≈4 Hz), not every frame.
 */
export function useAmhsDetailLevel(
  worldPosition: readonly [number, number, number],
  enabled = true
): AmhsDetailLevel {
  const [detail, setDetail] = useState<AmhsDetailLevel>('high');
  const tierRef = useRef<AmhsDetailLevel>('high');
  const accumRef = useRef(0);
  const posRef = useRef(new THREE.Vector3());

  useFrame((state, dt) => {
    if (!enabled) return;
    accumRef.current += dt;
    if (accumRef.current < AMHS_DETAIL_CHECK_INTERVAL_S) return;
    accumRef.current = 0;

    posRef.current.set(worldPosition[0], worldPosition[1], worldPosition[2]);
    const dist = horizontalDistanceM(
      state.camera.position.x,
      state.camera.position.z,
      posRef.current.x,
      posRef.current.z
    );
    const next = resolveAmhsDetailFromDistance(dist);
    if (next === tierRef.current) return;
    tierRef.current = next;
    setDetail(next);
  });

  return enabled ? detail : 'high';
}
